import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useSearchParams } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogDescription } from '../components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Alert, AlertDescription } from '../components/ui/alert';
import { ScrollArea } from '../components/ui/scroll-area';
import { Separator } from '../components/ui/separator';
import { toast } from 'sonner';
import { 
  Calculator, Users, FileText, Building2, CreditCard, 
  Download, Printer, Mail, ExternalLink, AlertTriangle,
  CheckCircle2, Clock, Calendar, PoundSterling, Play,
  RefreshCw, Shield, Globe, Briefcase, FileCheck,
  ChevronRight, Plus, Eye, Edit, Trash2, Search,
  Link2, Unlink, AlertCircle
} from 'lucide-react';

const MoneysoftPayrollPage = () => {
  const { api, user } = useAuth();
  const [searchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState('dashboard');
  const [employees, setEmployees] = useState([]);
  const [payslips, setPayslips] = useState([]);
  const [rtiSubmissions, setRtiSubmissions] = useState([]);
  const [rtwAlerts, setRtwAlerts] = useState({ expired: [], expiring_30_days: [], expiring_60_days: [], expiring_90_days: [] });
  const [taxRates, setTaxRates] = useState(null);
  const [externalLinks, setExternalLinks] = useState({});
  const [loading, setLoading] = useState(true);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [showEmployeeDialog, setShowEmployeeDialog] = useState(false);
  const [showPayslipDialog, setShowPayslipDialog] = useState(false);
  const [showRunPayrollDialog, setShowRunPayrollDialog] = useState(false);
  const [selectedPayslip, setSelectedPayslip] = useState(null);
  const [payrollMonth, setPayrollMonth] = useState(new Date().getMonth() + 1);
  const [payrollYear, setPayrollYear] = useState(new Date().getFullYear());
  const [summary, setSummary] = useState(null);
  const [staffList, setStaffList] = useState([]);
  
  // HMRC Connection State
  const [hmrcStatus, setHmrcStatus] = useState(null);
  const [connectingHmrc, setConnectingHmrc] = useState(false);

  const isDirector = user?.role === 'director';
  const isFinance = ['director', 'finance'].includes(user?.role);
  const isHR = ['director', 'hr', 'finance'].includes(user?.role);
  const canEdit = isFinance || user?.email === 'admin@icrcl.co.uk';

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const [empRes, linksRes, ratesRes, alertsRes, staffRes] = await Promise.all([
        api.get('/moneysoft-payroll/employees'),
        api.get('/moneysoft-payroll/links'),
        api.get('/moneysoft-payroll/tax-rates'),
        api.get('/moneysoft-payroll/rtw/alerts'),
        api.get('/staff')
      ]);
      setEmployees(empRes.data || []);
      setExternalLinks(linksRes.data || {});
      setTaxRates(ratesRes.data || {});
      setRtwAlerts(alertsRes.data || { expired: [], expiring_30_days: [], expiring_60_days: [], expiring_90_days: [] });
      setStaffList(staffRes.data || []);
    } catch (err) {
      console.error('Error fetching data:', err);
    } finally {
      setLoading(false);
    }
  }, [api]);

  const fetchPayslips = useCallback(async () => {
    try {
      const res = await api.get('/moneysoft-payroll/payslips');
      setPayslips(res.data || []);
    } catch (err) {
      console.error('Error fetching payslips:', err);
    }
  }, [api]);

  const fetchRTI = useCallback(async () => {
    try {
      const res = await api.get('/moneysoft-payroll/rti/submissions');
      setRtiSubmissions(res.data || []);
    } catch (err) {
      console.error('Error fetching RTI:', err);
    }
  }, [api]);

  const fetchSummary = useCallback(async () => {
    try {
      const res = await api.get(`/moneysoft-payroll/reports/summary?month=${payrollMonth}&year=${payrollYear}`);
      setSummary(res.data);
    } catch (err) {
      console.error('Error fetching summary:', err);
    }
  }, [api, payrollMonth, payrollYear]);

  useEffect(() => {
    fetchData();
    fetchPayslips();
    fetchRTI();
    fetchHmrcStatus();
    
    // Check for HMRC OAuth callback params
    const hmrcConnected = searchParams.get('hmrc_connected');
    const hmrcError = searchParams.get('hmrc_error');
    
    if (hmrcConnected === 'true') {
      toast.success('Successfully connected to HMRC!');
      fetchHmrcStatus();
    } else if (hmrcError) {
      toast.error(`HMRC connection failed: ${hmrcError}`);
    }
  }, [fetchData, fetchPayslips, fetchRTI]);
  
  const fetchHmrcStatus = async () => {
    try {
      const res = await api.get('/hmrc/status');
      setHmrcStatus(res.data);
    } catch (error) {
      console.error('Failed to fetch HMRC status:', error);
    }
  };
  
  const handleConnectHmrc = async () => {
    setConnectingHmrc(true);
    try {
      const res = await api.get('/hmrc/authorize');
      // Redirect to HMRC authorization page
      window.location.href = res.data.authorization_url;
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to initiate HMRC connection');
      setConnectingHmrc(false);
    }
  };
  
  const handleDisconnectHmrc = async () => {
    try {
      await api.post('/hmrc/disconnect');
      toast.success('Disconnected from HMRC');
      setHmrcStatus(prev => ({ ...prev, connected: false }));
    } catch (error) {
      toast.error('Failed to disconnect from HMRC');
    }
  };

  useEffect(() => {
    if (activeTab === 'dashboard') {
      fetchSummary();
    }
  }, [activeTab, payrollMonth, payrollYear, fetchSummary]);

  const handleRunPayroll = async () => {
    try {
      const res = await api.post(`/moneysoft-payroll/run-payroll?month=${payrollMonth}&year=${payrollYear}`);
      alert(`Payroll run complete!\nCreated: ${res.data.summary.created}\nSkipped: ${res.data.summary.skipped}\nErrors: ${res.data.summary.errors}`);
      setShowRunPayrollDialog(false);
      fetchPayslips();
      fetchSummary();
    } catch (err) {
      alert('Error running payroll: ' + (err.response?.data?.detail || err.message));
    }
  };

  const handleGenerateFPS = async () => {
    try {
      const res = await api.post(`/moneysoft-payroll/rti/fps?month=${payrollMonth}&year=${payrollYear}`);
      alert(`FPS Generated!\nEmployees: ${res.data.employees}\nTotal Pay: £${res.data.total_pay}\nTotal Tax: £${res.data.total_tax}`);
      fetchRTI();
    } catch (err) {
      alert('Error generating FPS: ' + (err.response?.data?.detail || err.message));
    }
  };

  const handleApprovePayslip = async (payslipId) => {
    try {
      await api.put(`/moneysoft-payroll/payslips/${payslipId}/approve`);
      fetchPayslips();
    } catch (err) {
      alert('Error approving payslip');
    }
  };

  const handleMarkPaid = async (payslipId) => {
    try {
      await api.put(`/moneysoft-payroll/payslips/${payslipId}/mark-paid`);
      fetchPayslips();
    } catch (err) {
      alert('Error marking payslip as paid');
    }
  };

  const formatCurrency = (amount) => `£${(amount || 0).toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

  const getStatusBadge = (status) => {
    const colors = {
      draft: 'bg-yellow-100 text-yellow-800',
      approved: 'bg-blue-100 text-blue-800',
      paid: 'bg-green-100 text-green-800',
      submitted_to_hmrc: 'bg-purple-100 text-purple-800'
    };
    return <Badge className={colors[status] || 'bg-gray-100'}>{status?.replace('_', ' ')}</Badge>;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="w-8 h-8 animate-spin text-[#1e3a5f]" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="moneysoft-payroll-page">
      {/* HMRC Connection Status Card */}
      <Card className={`border-2 ${
        hmrcStatus?.connected 
          ? (hmrcStatus?.environment === 'live' 
              ? 'border-red-400 bg-red-50' 
              : 'border-green-300 bg-green-50') 
          : 'border-amber-300 bg-amber-50'
      }`}>
        <CardContent className="pt-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className={`p-3 rounded-full ${hmrcStatus?.connected ? 'bg-green-100' : 'bg-amber-100'}`}>
                <Shield className={`w-6 h-6 ${hmrcStatus?.connected ? 'text-green-600' : 'text-amber-600'}`} />
              </div>
              <div>
                <h3 className="font-semibold text-lg flex items-center gap-2">
                  HMRC RTI Integration
                  {hmrcStatus?.connected ? (
                    <Badge className="bg-green-500">
                      <CheckCircle2 className="w-3 h-3 mr-1" />
                      Connected
                    </Badge>
                  ) : (
                    <Badge variant="outline" className="border-amber-500 text-amber-700">
                      <AlertCircle className="w-3 h-3 mr-1" />
                      Not Connected
                    </Badge>
                  )}
                </h3>
                <p className="text-sm text-slate-600">
                  {hmrcStatus?.connected 
                    ? 'Real-time payroll submissions to HMRC are enabled'
                    : 'Connect to HMRC to enable real-time payroll submissions (FPS, EPS)'}
                </p>
                {hmrcStatus?.environment && (
                  <div className="flex items-center gap-3 mt-1">
                    <div className={`px-2 py-0.5 rounded text-xs font-bold ${
                      hmrcStatus.environment === 'live' 
                        ? 'bg-red-500 text-white' 
                        : 'bg-green-500 text-white'
                    }`}>
                      {hmrcStatus.environment === 'live' ? '🔴 LIVE MODE' : '🟢 TEST MODE'}
                    </div>
                    <button
                      onClick={async () => {
                        const isCurrentlyLive = hmrcStatus?.environment === 'live';
                        const newEnv = isCurrentlyLive ? 'test' : 'live';
                        try {
                          await api.post('/hmrc/set-environment', { environment: newEnv });
                          toast.success(`Switched to ${newEnv.toUpperCase()} mode`);
                          // Refresh status to update UI
                          const statusRes = await api.get('/hmrc/status');
                          setHmrcStatus(statusRes.data);
                        } catch (err) {
                          toast.error('Failed to switch environment');
                        }
                      }}
                      className={`px-3 py-1 text-xs font-medium rounded transition-colors ${
                        hmrcStatus.environment === 'live' 
                          ? 'bg-green-100 text-green-700 hover:bg-green-200 border border-green-300' 
                          : 'bg-red-100 text-red-700 hover:bg-red-200 border border-red-300'
                      }`}
                    >
                      {hmrcStatus.environment === 'live' 
                        ? '🟢 Switch to TEST' 
                        : '🔴 Switch to LIVE'}
                    </button>
                    {hmrcStatus?.token_info?.expires_at && (
                      <span className="text-xs text-slate-500">
                        Token expires: {new Date(hmrcStatus.token_info.expires_at).toLocaleString('en-GB')}
                      </span>
                    )}
                  </div>
                )}
              </div>
            </div>
            <div className="flex gap-2">
              {hmrcStatus?.connected ? (
                <>
                  <Button variant="outline" size="sm" onClick={handleDisconnectHmrc}>
                    <Unlink className="w-4 h-4 mr-1" />
                    Disconnect
                  </Button>
                  <Button 
                    variant="outline" 
                    size="sm" 
                    onClick={async () => {
                      try {
                        const res = await api.post('/hmrc/test-connection');
                        if (res.data.success) {
                          toast.success('Connection test successful!');
                        } else {
                          toast.error(res.data.message || 'Test failed');
                        }
                      } catch (err) {
                        toast.error('Test failed');
                      }
                    }}
                    className="border-blue-300 text-blue-600 hover:bg-blue-50"
                  >
                    <Play className="w-4 h-4 mr-1" />
                    Test
                  </Button>
                  <Button size="sm" className="bg-green-600 hover:bg-green-700" onClick={() => setActiveTab('hmrc')}>
                    <FileText className="w-4 h-4 mr-1" />
                    Submit RTI
                  </Button>
                </>
              ) : (
                <Button 
                  onClick={handleConnectHmrc} 
                  disabled={connectingHmrc || !hmrcStatus?.configured}
                  className="bg-[#1e3a5f] hover:bg-[#1e3a5f]/90"
                >
                  {connectingHmrc ? (
                    <>Connecting...</>
                  ) : (
                    <>
                      <Link2 className="w-4 h-4 mr-1" />
                      Connect to HMRC
                      <ExternalLink className="w-3 h-3 ml-1" />
                    </>
                  )}
                </Button>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-[#1e3a5f]">Payroll Manager</h1>
          <p className="text-slate-500">Moneysoft-style UK compliant payroll system</p>
        </div>
        <div className="flex gap-2">
          {canEdit && (
            <Button onClick={() => setShowRunPayrollDialog(true)} className="bg-[#2a9d8f]" data-testid="run-payroll-btn">
              <Play className="w-4 h-4 mr-2" />
              Run Payroll
            </Button>
          )}
          <Button variant="outline" onClick={fetchData}>
            <RefreshCw className="w-4 h-4 mr-2" />
            Refresh
          </Button>
        </div>
      </div>

      {/* RTW Alerts Banner */}
      {(rtwAlerts.expired?.length > 0 || rtwAlerts.expiring_30_days?.length > 0) && (
        <Alert className="border-red-200 bg-red-50">
          <AlertTriangle className="w-4 h-4 text-red-600" />
          <AlertDescription className="text-red-800">
            <strong>Right to Work Alerts:</strong> 
            {rtwAlerts.expired?.length > 0 && ` ${rtwAlerts.expired.length} expired`}
            {rtwAlerts.expiring_30_days?.length > 0 && ` ${rtwAlerts.expiring_30_days.length} expiring within 30 days`}
          </AlertDescription>
        </Alert>
      )}

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid grid-cols-6 w-full max-w-4xl">
          <TabsTrigger value="dashboard">Dashboard</TabsTrigger>
          <TabsTrigger value="employees">Employees</TabsTrigger>
          <TabsTrigger value="payslips">Payslips</TabsTrigger>
          <TabsTrigger value="hmrc">HMRC & RTI</TabsTrigger>
          <TabsTrigger value="pension">Pension</TabsTrigger>
          <TabsTrigger value="rtw">Right to Work</TabsTrigger>
        </TabsList>

        {/* Dashboard Tab */}
        <TabsContent value="dashboard" className="space-y-6">
          {/* Period Selector */}
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center gap-4">
                <Label>Pay Period:</Label>
                <Select value={payrollMonth.toString()} onValueChange={(v) => setPayrollMonth(parseInt(v))}>
                  <SelectTrigger className="w-32">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {[...Array(12)].map((_, i) => (
                      <SelectItem key={i + 1} value={(i + 1).toString()}>
                        {new Date(2024, i).toLocaleString('default', { month: 'long' })}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Select value={payrollYear.toString()} onValueChange={(v) => setPayrollYear(parseInt(v))}>
                  <SelectTrigger className="w-24">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="2024">2024</SelectItem>
                    <SelectItem value="2025">2025</SelectItem>
                  </SelectContent>
                </Select>
                <Button variant="outline" size="sm" onClick={fetchSummary}>
                  <Search className="w-4 h-4 mr-1" /> View
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* Summary Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-slate-500">Employees on Payroll</p>
                    <p className="text-2xl font-bold">{employees.length}</p>
                  </div>
                  <Users className="w-8 h-8 text-[#2a9d8f]" />
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-slate-500">Gross Pay ({payrollMonth}/{payrollYear})</p>
                    <p className="text-2xl font-bold">{formatCurrency(summary?.total_gross_pay)}</p>
                  </div>
                  <PoundSterling className="w-8 h-8 text-[#1e3a5f]" />
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-slate-500">Total Tax + NI</p>
                    <p className="text-2xl font-bold">{formatCurrency((summary?.total_income_tax || 0) + (summary?.total_ni_employee || 0))}</p>
                  </div>
                  <Building2 className="w-8 h-8 text-amber-600" />
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-slate-500">Net Pay Total</p>
                    <p className="text-2xl font-bold text-green-600">{formatCurrency(summary?.total_net_pay)}</p>
                  </div>
                  <CreditCard className="w-8 h-8 text-green-600" />
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Tax Rates Reference */}
          {taxRates && (
            <Card>
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <Calculator className="w-5 h-5" />
                  UK Tax Rates {taxRates.tax_year}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                  <div>
                    <p className="text-slate-500">Personal Allowance</p>
                    <p className="font-medium">£{taxRates.personal_allowance?.toLocaleString()}</p>
                  </div>
                  <div>
                    <p className="text-slate-500">Basic Rate (20%)</p>
                    <p className="font-medium">Up to £{taxRates.basic_rate?.threshold?.toLocaleString()}</p>
                  </div>
                  <div>
                    <p className="text-slate-500">Higher Rate (40%)</p>
                    <p className="font-medium">Up to £{taxRates.higher_rate?.threshold?.toLocaleString()}</p>
                  </div>
                  <div>
                    <p className="text-slate-500">Additional Rate</p>
                    <p className="font-medium">{taxRates.additional_rate?.rate}%</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Quick Links */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <Globe className="w-5 h-5" />
                Quick Links
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <a href={externalLinks.hmrc_main} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 p-3 rounded-lg border hover:bg-slate-50 transition-colors">
                  <Building2 className="w-5 h-5 text-[#1e3a5f]" />
                  <span className="text-sm">HMRC Main</span>
                  <ExternalLink className="w-3 h-3 ml-auto text-slate-400" />
                </a>
                <a href={externalLinks.hmrc_paye} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 p-3 rounded-lg border hover:bg-slate-50 transition-colors">
                  <FileText className="w-5 h-5 text-[#2a9d8f]" />
                  <span className="text-sm">PAYE Online</span>
                  <ExternalLink className="w-3 h-3 ml-auto text-slate-400" />
                </a>
                <a href={externalLinks.hmrc_rti} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 p-3 rounded-lg border hover:bg-slate-50 transition-colors">
                  <FileCheck className="w-5 h-5 text-purple-600" />
                  <span className="text-sm">RTI Reporting</span>
                  <ExternalLink className="w-3 h-3 ml-auto text-slate-400" />
                </a>
                <a href={externalLinks.home_office_rtw} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 p-3 rounded-lg border hover:bg-slate-50 transition-colors">
                  <Shield className="w-5 h-5 text-red-600" />
                  <span className="text-sm">Right to Work</span>
                  <ExternalLink className="w-3 h-3 ml-auto text-slate-400" />
                </a>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Employees Tab */}
        <TabsContent value="employees" className="space-y-4">
          <div className="flex justify-between items-center">
            <h2 className="text-lg font-semibold">Employee Payroll Setup</h2>
            {canEdit && (
              <Button onClick={() => { setSelectedEmployee(null); setShowEmployeeDialog(true); }}>
                <Plus className="w-4 h-4 mr-2" />
                Add Employee to Payroll
              </Button>
            )}
          </div>
          
          <Card>
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Employee</TableHead>
                    <TableHead>Salary/Rate</TableHead>
                    <TableHead>Pay Frequency</TableHead>
                    <TableHead>Tax Code</TableHead>
                    <TableHead>NI Cat</TableHead>
                    <TableHead>Pension</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {employees.map((emp) => (
                    <TableRow key={emp.staff_id}>
                      <TableCell>
                        <div>
                          <p className="font-medium">{emp.staff_name}</p>
                          <p className="text-xs text-slate-500">{emp.staff_email}</p>
                        </div>
                      </TableCell>
                      <TableCell>
                        {emp.payment_type === 'salary' 
                          ? `£${emp.annual_salary?.toLocaleString()}/yr`
                          : `£${emp.hourly_rate}/hr`
                        }
                      </TableCell>
                      <TableCell className="capitalize">{emp.pay_frequency?.replace('_', ' ')}</TableCell>
                      <TableCell><Badge variant="outline">{emp.tax_code}</Badge></TableCell>
                      <TableCell>{emp.ni_category}</TableCell>
                      <TableCell>
                        {emp.pension_enrolled 
                          ? <Badge className="bg-green-100 text-green-800">Enrolled</Badge>
                          : <Badge variant="secondary">Opted Out</Badge>
                        }
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex justify-end gap-1">
                          <Button variant="ghost" size="sm" onClick={() => { setSelectedEmployee(emp); setShowEmployeeDialog(true); }}>
                            <Eye className="w-4 h-4" />
                          </Button>
                          {canEdit && (
                            <Button variant="ghost" size="sm" onClick={() => { setSelectedEmployee(emp); setShowEmployeeDialog(true); }}>
                              <Edit className="w-4 h-4" />
                            </Button>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                  {employees.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={7} className="text-center py-8 text-slate-500">
                        No employees on payroll yet. Click "Add Employee to Payroll" to get started.
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Payslips Tab */}
        <TabsContent value="payslips" className="space-y-4">
          <div className="flex justify-between items-center">
            <h2 className="text-lg font-semibold">Payslips</h2>
            <div className="flex gap-2">
              <Select value={payrollMonth.toString()} onValueChange={(v) => setPayrollMonth(parseInt(v))}>
                <SelectTrigger className="w-32">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {[...Array(12)].map((_, i) => (
                    <SelectItem key={i + 1} value={(i + 1).toString()}>
                      {new Date(2024, i).toLocaleString('default', { month: 'short' })}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <Card>
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Payslip #</TableHead>
                    <TableHead>Employee</TableHead>
                    <TableHead>Period</TableHead>
                    <TableHead className="text-right">Gross</TableHead>
                    <TableHead className="text-right">Tax</TableHead>
                    <TableHead className="text-right">NI</TableHead>
                    <TableHead className="text-right">Net Pay</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {payslips.map((ps) => (
                    <TableRow key={ps.id}>
                      <TableCell className="font-mono text-sm">{ps.payslip_number}</TableCell>
                      <TableCell>{ps.staff_name}</TableCell>
                      <TableCell className="text-sm">{ps.period_start?.slice(0, 10)}</TableCell>
                      <TableCell className="text-right">{formatCurrency(ps.gross_pay)}</TableCell>
                      <TableCell className="text-right text-red-600">{formatCurrency(ps.income_tax)}</TableCell>
                      <TableCell className="text-right text-amber-600">{formatCurrency(ps.national_insurance)}</TableCell>
                      <TableCell className="text-right font-medium text-green-600">{formatCurrency(ps.net_pay)}</TableCell>
                      <TableCell>{getStatusBadge(ps.status)}</TableCell>
                      <TableCell className="text-right">
                        <div className="flex justify-end gap-1">
                          <Button variant="ghost" size="sm" onClick={() => { setSelectedPayslip(ps); setShowPayslipDialog(true); }}>
                            <Eye className="w-4 h-4" />
                          </Button>
                          {canEdit && ps.status === 'draft' && (
                            <Button variant="ghost" size="sm" onClick={() => handleApprovePayslip(ps.id)}>
                              <CheckCircle2 className="w-4 h-4 text-green-600" />
                            </Button>
                          )}
                          {canEdit && ps.status === 'approved' && (
                            <Button variant="ghost" size="sm" onClick={() => handleMarkPaid(ps.id)}>
                              <CreditCard className="w-4 h-4 text-blue-600" />
                            </Button>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                  {payslips.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={9} className="text-center py-8 text-slate-500">
                        No payslips found. Run payroll to generate payslips.
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        {/* HMRC Tab */}
        <TabsContent value="hmrc" className="space-y-6">
          <div className="grid md:grid-cols-2 gap-6">
            {/* FPS Submission */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <FileCheck className="w-5 h-5" />
                  Full Payment Submission (FPS)
                </CardTitle>
                <CardDescription>Submit payroll data to HMRC via RTI</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex gap-2">
                  <Select value={payrollMonth.toString()} onValueChange={(v) => setPayrollMonth(parseInt(v))}>
                    <SelectTrigger className="w-full">
                      <SelectValue placeholder="Month" />
                    </SelectTrigger>
                    <SelectContent>
                      {[...Array(12)].map((_, i) => (
                        <SelectItem key={i + 1} value={(i + 1).toString()}>
                          {new Date(2024, i).toLocaleString('default', { month: 'long' })}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Select value={payrollYear.toString()} onValueChange={(v) => setPayrollYear(parseInt(v))}>
                    <SelectTrigger className="w-24">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="2024">2024</SelectItem>
                      <SelectItem value="2025">2025</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                {canEdit && (
                  <Button onClick={handleGenerateFPS} className="w-full">
                    <FileText className="w-4 h-4 mr-2" />
                    Generate FPS
                  </Button>
                )}
                <a href={externalLinks.hmrc_paye} target="_blank" rel="noopener noreferrer">
                  <Button variant="outline" className="w-full">
                    <ExternalLink className="w-4 h-4 mr-2" />
                    Submit via HMRC Gateway
                  </Button>
                </a>
              </CardContent>
            </Card>

            {/* P45/P60 */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <FileText className="w-5 h-5" />
                  P45 / P60 Documents
                </CardTitle>
                <CardDescription>Generate leaver and end-of-year documents</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <Button variant="outline" className="w-full justify-start">
                  <FileText className="w-4 h-4 mr-2" />
                  Generate P45 (Leaver)
                  <ChevronRight className="w-4 h-4 ml-auto" />
                </Button>
                <Button variant="outline" className="w-full justify-start">
                  <FileText className="w-4 h-4 mr-2" />
                  Generate P60 (End of Year)
                  <ChevronRight className="w-4 h-4 ml-auto" />
                </Button>
              </CardContent>
            </Card>
          </div>

          {/* RTI Submission History */}
          <Card>
            <CardHeader>
              <CardTitle>RTI Submission History</CardTitle>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Type</TableHead>
                    <TableHead>Period</TableHead>
                    <TableHead>Employees</TableHead>
                    <TableHead className="text-right">Total Pay</TableHead>
                    <TableHead className="text-right">Total Tax</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Submitted</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {rtiSubmissions.map((sub) => (
                    <TableRow key={sub.id}>
                      <TableCell><Badge>{sub.submission_type}</Badge></TableCell>
                      <TableCell>Period {sub.period} / {sub.tax_year}</TableCell>
                      <TableCell>{sub.employees_count}</TableCell>
                      <TableCell className="text-right">{formatCurrency(sub.total_pay)}</TableCell>
                      <TableCell className="text-right">{formatCurrency(sub.total_tax)}</TableCell>
                      <TableCell>
                        <Badge className={sub.status === 'generated' ? 'bg-yellow-100 text-yellow-800' : 'bg-green-100 text-green-800'}>
                          {sub.status}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-sm">{sub.submission_date?.slice(0, 10)}</TableCell>
                    </TableRow>
                  ))}
                  {rtiSubmissions.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={7} className="text-center py-8 text-slate-500">
                        No RTI submissions yet
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Pension Tab */}
        <TabsContent value="pension" className="space-y-6">
          <div className="grid md:grid-cols-3 gap-4">
            <a href={externalLinks.nest} target="_blank" rel="noopener noreferrer">
              <Card className="hover:shadow-md transition-shadow cursor-pointer h-full">
                <CardContent className="pt-6">
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 rounded-lg bg-green-100 flex items-center justify-center">
                      <Briefcase className="w-6 h-6 text-green-600" />
                    </div>
                    <div>
                      <h3 className="font-semibold">NEST</h3>
                      <p className="text-sm text-slate-500">National Employment Savings Trust</p>
                    </div>
                    <ExternalLink className="w-4 h-4 ml-auto text-slate-400" />
                  </div>
                </CardContent>
              </Card>
            </a>
            <a href={externalLinks.now_pensions} target="_blank" rel="noopener noreferrer">
              <Card className="hover:shadow-md transition-shadow cursor-pointer h-full">
                <CardContent className="pt-6">
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 rounded-lg bg-blue-100 flex items-center justify-center">
                      <Briefcase className="w-6 h-6 text-blue-600" />
                    </div>
                    <div>
                      <h3 className="font-semibold">NOW: Pensions</h3>
                      <p className="text-sm text-slate-500">Workplace pension provider</p>
                    </div>
                    <ExternalLink className="w-4 h-4 ml-auto text-slate-400" />
                  </div>
                </CardContent>
              </Card>
            </a>
            <a href={externalLinks.peoples_pension} target="_blank" rel="noopener noreferrer">
              <Card className="hover:shadow-md transition-shadow cursor-pointer h-full">
                <CardContent className="pt-6">
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 rounded-lg bg-purple-100 flex items-center justify-center">
                      <Briefcase className="w-6 h-6 text-purple-600" />
                    </div>
                    <div>
                      <h3 className="font-semibold">The People's Pension</h3>
                      <p className="text-sm text-slate-500">Not-for-profit provider</p>
                    </div>
                    <ExternalLink className="w-4 h-4 ml-auto text-slate-400" />
                  </div>
                </CardContent>
              </Card>
            </a>
          </div>

          <Card>
            <CardHeader>
              <CardTitle>Pension Contributions Export</CardTitle>
              <CardDescription>Export pension data for provider upload</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex gap-4">
                <div className="flex-1">
                  <Label>Period</Label>
                  <div className="flex gap-2 mt-1">
                    <Select value={payrollMonth.toString()} onValueChange={(v) => setPayrollMonth(parseInt(v))}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {[...Array(12)].map((_, i) => (
                          <SelectItem key={i + 1} value={(i + 1).toString()}>
                            {new Date(2024, i).toLocaleString('default', { month: 'long' })}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <Select value={payrollYear.toString()} onValueChange={(v) => setPayrollYear(parseInt(v))}>
                      <SelectTrigger className="w-24">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="2024">2024</SelectItem>
                        <SelectItem value="2025">2025</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div>
                  <Label>Provider</Label>
                  <Select defaultValue="nest">
                    <SelectTrigger className="w-40 mt-1">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="nest">NEST</SelectItem>
                      <SelectItem value="now_pensions">NOW: Pensions</SelectItem>
                      <SelectItem value="peoples_pension">People's Pension</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <Button className="w-full">
                <Download className="w-4 h-4 mr-2" />
                Export Pension Data
              </Button>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Right to Work Tab */}
        <TabsContent value="rtw" className="space-y-6">
          <div className="grid md:grid-cols-4 gap-4">
            <Card className={rtwAlerts.expired?.length > 0 ? 'border-red-300 bg-red-50' : ''}>
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-slate-500">Expired</p>
                    <p className="text-2xl font-bold text-red-600">{rtwAlerts.expired?.length || 0}</p>
                  </div>
                  <AlertTriangle className="w-8 h-8 text-red-500" />
                </div>
              </CardContent>
            </Card>
            <Card className={rtwAlerts.expiring_30_days?.length > 0 ? 'border-amber-300 bg-amber-50' : ''}>
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-slate-500">Expiring (30 days)</p>
                    <p className="text-2xl font-bold text-amber-600">{rtwAlerts.expiring_30_days?.length || 0}</p>
                  </div>
                  <Clock className="w-8 h-8 text-amber-500" />
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-slate-500">Expiring (60 days)</p>
                    <p className="text-2xl font-bold">{rtwAlerts.expiring_60_days?.length || 0}</p>
                  </div>
                  <Clock className="w-8 h-8 text-slate-400" />
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-slate-500">Expiring (90 days)</p>
                    <p className="text-2xl font-bold">{rtwAlerts.expiring_90_days?.length || 0}</p>
                  </div>
                  <Calendar className="w-8 h-8 text-slate-400" />
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Alerts List */}
          {(rtwAlerts.expired?.length > 0 || rtwAlerts.expiring_30_days?.length > 0) && (
            <Card>
              <CardHeader>
                <CardTitle className="text-red-600 flex items-center gap-2">
                  <AlertTriangle className="w-5 h-5" />
                  Urgent: Right to Work Action Required
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {rtwAlerts.expired?.map((alert) => (
                    <div key={alert.staff_id} className="flex items-center justify-between p-3 bg-red-50 rounded-lg border border-red-200">
                      <div>
                        <p className="font-medium">{alert.staff_name}</p>
                        <p className="text-sm text-red-600">
                          {alert.visa_type?.replace('_', ' ')} - Expired {Math.abs(alert.days_until_expiry)} days ago
                        </p>
                      </div>
                      <Button variant="destructive" size="sm">
                        <Shield className="w-4 h-4 mr-2" />
                        Record Check
                      </Button>
                    </div>
                  ))}
                  {rtwAlerts.expiring_30_days?.map((alert) => (
                    <div key={alert.staff_id} className="flex items-center justify-between p-3 bg-amber-50 rounded-lg border border-amber-200">
                      <div>
                        <p className="font-medium">{alert.staff_name}</p>
                        <p className="text-sm text-amber-600">
                          {alert.visa_type?.replace('_', ' ')} - Expires in {alert.days_until_expiry} days
                        </p>
                      </div>
                      <Button variant="outline" size="sm">
                        <Shield className="w-4 h-4 mr-2" />
                        Record Check
                      </Button>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          <Card>
            <CardContent className="pt-6">
              <a href={externalLinks.home_office_rtw} target="_blank" rel="noopener noreferrer">
                <Button variant="outline" className="w-full">
                  <ExternalLink className="w-4 h-4 mr-2" />
                  Home Office Online Right to Work Check Service
                </Button>
              </a>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Run Payroll Dialog */}
      <Dialog open={showRunPayrollDialog} onOpenChange={setShowRunPayrollDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Run Payroll</DialogTitle>
            <DialogDescription>Generate payslips for all active employees</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Month</Label>
                <Select value={payrollMonth.toString()} onValueChange={(v) => setPayrollMonth(parseInt(v))}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {[...Array(12)].map((_, i) => (
                      <SelectItem key={i + 1} value={(i + 1).toString()}>
                        {new Date(2024, i).toLocaleString('default', { month: 'long' })}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Year</Label>
                <Select value={payrollYear.toString()} onValueChange={(v) => setPayrollYear(parseInt(v))}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="2024">2024</SelectItem>
                    <SelectItem value="2025">2025</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <Alert>
              <AlertDescription>
                This will generate payslips for {employees.length} employee(s) for {new Date(2024, payrollMonth - 1).toLocaleString('default', { month: 'long' })} {payrollYear}.
                Existing payslips for this period will be skipped.
              </AlertDescription>
            </Alert>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowRunPayrollDialog(false)}>Cancel</Button>
            <Button onClick={handleRunPayroll} className="bg-[#2a9d8f]">
              <Play className="w-4 h-4 mr-2" />
              Run Payroll
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Payslip Detail Dialog */}
      <Dialog open={showPayslipDialog} onOpenChange={setShowPayslipDialog}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Payslip Details</DialogTitle>
          </DialogHeader>
          {selectedPayslip && (
            <div className="space-y-4">
              <div className="flex justify-between items-start">
                <div>
                  <p className="font-semibold text-lg">{selectedPayslip.staff_name}</p>
                  <p className="text-sm text-slate-500">{selectedPayslip.payslip_number}</p>
                </div>
                {getStatusBadge(selectedPayslip.status)}
              </div>
              <Separator />
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <p className="text-slate-500">Period</p>
                  <p className="font-medium">{selectedPayslip.period_start} to {selectedPayslip.period_end}</p>
                </div>
                <div>
                  <p className="text-slate-500">Tax Code</p>
                  <p className="font-medium">{selectedPayslip.tax_code}</p>
                </div>
              </div>
              <Separator />
              <div className="grid grid-cols-2 gap-6">
                <div className="space-y-2">
                  <h4 className="font-semibold text-green-700">Earnings</h4>
                  <div className="flex justify-between"><span>Basic Pay</span><span>{formatCurrency(selectedPayslip.basic_pay)}</span></div>
                  {selectedPayslip.overtime_pay > 0 && <div className="flex justify-between"><span>Overtime</span><span>{formatCurrency(selectedPayslip.overtime_pay)}</span></div>}
                  {selectedPayslip.bonus > 0 && <div className="flex justify-between"><span>Bonus</span><span>{formatCurrency(selectedPayslip.bonus)}</span></div>}
                  <div className="flex justify-between font-semibold border-t pt-2"><span>Gross Pay</span><span>{formatCurrency(selectedPayslip.gross_pay)}</span></div>
                </div>
                <div className="space-y-2">
                  <h4 className="font-semibold text-red-700">Deductions</h4>
                  <div className="flex justify-between"><span>Income Tax</span><span>-{formatCurrency(selectedPayslip.income_tax)}</span></div>
                  <div className="flex justify-between"><span>National Insurance</span><span>-{formatCurrency(selectedPayslip.national_insurance)}</span></div>
                  {selectedPayslip.pension_employee > 0 && <div className="flex justify-between"><span>Pension</span><span>-{formatCurrency(selectedPayslip.pension_employee)}</span></div>}
                  {selectedPayslip.student_loan > 0 && <div className="flex justify-between"><span>Student Loan</span><span>-{formatCurrency(selectedPayslip.student_loan)}</span></div>}
                  <div className="flex justify-between font-semibold border-t pt-2"><span>Total Deductions</span><span>-{formatCurrency(selectedPayslip.total_deductions)}</span></div>
                </div>
              </div>
              <div className="bg-[#1e3a5f] text-white p-4 rounded-lg flex justify-between items-center">
                <span className="font-semibold">Net Pay</span>
                <span className="text-2xl font-bold">{formatCurrency(selectedPayslip.net_pay)}</span>
              </div>
              <div className="text-sm text-slate-500 grid grid-cols-2 gap-2">
                <div><span>YTD Gross:</span> <span className="font-medium">{formatCurrency(selectedPayslip.ytd_gross)}</span></div>
                <div><span>YTD Tax:</span> <span className="font-medium">{formatCurrency(selectedPayslip.ytd_tax)}</span></div>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline">
              <Download className="w-4 h-4 mr-2" />
              Download PDF
            </Button>
            <Button variant="outline">
              <Printer className="w-4 h-4 mr-2" />
              Print
            </Button>
            <Button variant="outline">
              <Mail className="w-4 h-4 mr-2" />
              Email
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Employee Setup Dialog */}
      <Dialog open={showEmployeeDialog} onOpenChange={setShowEmployeeDialog}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{selectedEmployee ? 'Edit Employee Payroll' : 'Add Employee to Payroll'}</DialogTitle>
          </DialogHeader>
          <EmployeePayrollForm 
            employee={selectedEmployee}
            staffList={staffList.filter(s => !employees.some(e => e.staff_id === s.id))}
            onSave={async (data) => {
              try {
                await api.post('/moneysoft-payroll/employees', data);
                setShowEmployeeDialog(false);
                fetchData();
              } catch (err) {
                alert('Error saving employee: ' + (err.response?.data?.detail || err.message));
              }
            }}
            onClose={() => setShowEmployeeDialog(false)}
            canEdit={canEdit}
          />
        </DialogContent>
      </Dialog>
    </div>
  );
};

// Employee Payroll Form Component
const EmployeePayrollForm = ({ employee, staffList, onSave, onClose, canEdit }) => {
  const [formData, setFormData] = useState({
    staff_id: employee?.staff_id || '',
    payment_type: employee?.payment_type || 'salary',
    annual_salary: employee?.annual_salary || 30000,
    hourly_rate: employee?.hourly_rate || 15,
    pay_frequency: employee?.pay_frequency || 'monthly',
    contracted_hours: employee?.contracted_hours || 37.5,
    tax_code: employee?.tax_code || '1257L',
    ni_category: employee?.ni_category || 'A',
    ni_number: employee?.ni_number || '',
    pension_enrolled: employee?.pension_enrolled ?? true,
    pension_employee_percent: employee?.pension_employee_percent || 5,
    pension_employer_percent: employee?.pension_employer_percent || 3,
    pension_provider: employee?.pension_provider || 'nest',
    student_loan_plan: employee?.student_loan_plan || 'none',
    postgrad_loan: employee?.postgrad_loan || false,
    bank_name: employee?.bank_name || '',
    account_number: employee?.account_number || '',
    sort_code: employee?.sort_code || '',
    visa_type: employee?.visa_type || 'british_citizen',
    visa_expiry: employee?.visa_expiry || '',
    share_code: employee?.share_code || ''
  });

  const handleSubmit = () => {
    if (!formData.staff_id) {
      alert('Please select an employee');
      return;
    }
    onSave(formData);
  };

  return (
    <div className="space-y-6">
      {/* Employee Selection */}
      {!employee && (
        <div>
          <Label>Select Employee *</Label>
          <Select value={formData.staff_id} onValueChange={(v) => setFormData({...formData, staff_id: v})}>
            <SelectTrigger>
              <SelectValue placeholder="Select staff member" />
            </SelectTrigger>
            <SelectContent>
              {staffList.map((staff) => (
                <SelectItem key={staff.id} value={staff.id}>
                  {staff.first_name} {staff.last_name} ({staff.job_title})
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      )}

      {/* Pay Details */}
      <div className="space-y-4">
        <h4 className="font-semibold text-slate-700">Pay Details</h4>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <Label>Payment Type</Label>
            <Select value={formData.payment_type} onValueChange={(v) => setFormData({...formData, payment_type: v})} disabled={!canEdit}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="salary">Annual Salary</SelectItem>
                <SelectItem value="hourly">Hourly Rate</SelectItem>
              </SelectContent>
            </Select>
          </div>
          {formData.payment_type === 'salary' ? (
            <div>
              <Label>Annual Salary (£)</Label>
              <Input type="number" value={formData.annual_salary} onChange={(e) => setFormData({...formData, annual_salary: parseFloat(e.target.value)})} disabled={!canEdit} />
            </div>
          ) : (
            <div>
              <Label>Hourly Rate (£)</Label>
              <Input type="number" value={formData.hourly_rate} onChange={(e) => setFormData({...formData, hourly_rate: parseFloat(e.target.value)})} disabled={!canEdit} />
            </div>
          )}
          <div>
            <Label>Pay Frequency</Label>
            <Select value={formData.pay_frequency} onValueChange={(v) => setFormData({...formData, pay_frequency: v})} disabled={!canEdit}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="weekly">Weekly</SelectItem>
                <SelectItem value="fortnightly">Fortnightly</SelectItem>
                <SelectItem value="four_weekly">4-Weekly</SelectItem>
                <SelectItem value="monthly">Monthly</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Contracted Hours/Week</Label>
            <Input type="number" value={formData.contracted_hours} onChange={(e) => setFormData({...formData, contracted_hours: parseFloat(e.target.value)})} disabled={!canEdit} />
          </div>
        </div>
      </div>

      <Separator />

      {/* Tax & NI */}
      <div className="space-y-4">
        <h4 className="font-semibold text-slate-700">Tax & National Insurance</h4>
        <div className="grid grid-cols-3 gap-4">
          <div>
            <Label>Tax Code</Label>
            <Input value={formData.tax_code} onChange={(e) => setFormData({...formData, tax_code: e.target.value.toUpperCase()})} disabled={!canEdit} />
          </div>
          <div>
            <Label>NI Category</Label>
            <Select value={formData.ni_category} onValueChange={(v) => setFormData({...formData, ni_category: v})} disabled={!canEdit}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="A">A - Standard</SelectItem>
                <SelectItem value="B">B - Married Women Reduced</SelectItem>
                <SelectItem value="C">C - Over Pension Age</SelectItem>
                <SelectItem value="H">H - Apprentice Under 25</SelectItem>
                <SelectItem value="M">M - Under 21</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>NI Number</Label>
            <Input value={formData.ni_number} onChange={(e) => setFormData({...formData, ni_number: e.target.value.toUpperCase()})} placeholder="AB123456C" disabled={!canEdit} />
          </div>
        </div>
      </div>

      <Separator />

      {/* Pension */}
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <h4 className="font-semibold text-slate-700">Pension Auto-Enrollment</h4>
          <div className="flex items-center gap-2">
            <input type="checkbox" checked={formData.pension_enrolled} onChange={(e) => setFormData({...formData, pension_enrolled: e.target.checked})} disabled={!canEdit} />
            <Label>Enrolled</Label>
          </div>
        </div>
        {formData.pension_enrolled && (
          <div className="grid grid-cols-3 gap-4">
            <div>
              <Label>Provider</Label>
              <Select value={formData.pension_provider} onValueChange={(v) => setFormData({...formData, pension_provider: v})} disabled={!canEdit}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="nest">NEST</SelectItem>
                  <SelectItem value="now_pensions">NOW: Pensions</SelectItem>
                  <SelectItem value="peoples_pension">People's Pension</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Employee %</Label>
              <Input type="number" value={formData.pension_employee_percent} onChange={(e) => setFormData({...formData, pension_employee_percent: parseFloat(e.target.value)})} disabled={!canEdit} />
            </div>
            <div>
              <Label>Employer %</Label>
              <Input type="number" value={formData.pension_employer_percent} onChange={(e) => setFormData({...formData, pension_employer_percent: parseFloat(e.target.value)})} disabled={!canEdit} />
            </div>
          </div>
        )}
      </div>

      <Separator />

      {/* Student Loan */}
      <div className="space-y-4">
        <h4 className="font-semibold text-slate-700">Student Loan</h4>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <Label>Plan Type</Label>
            <Select value={formData.student_loan_plan} onValueChange={(v) => setFormData({...formData, student_loan_plan: v})} disabled={!canEdit}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="none">None</SelectItem>
                <SelectItem value="plan1">Plan 1</SelectItem>
                <SelectItem value="plan2">Plan 2</SelectItem>
                <SelectItem value="plan4">Plan 4 (Scotland)</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="flex items-end">
            <div className="flex items-center gap-2">
              <input type="checkbox" checked={formData.postgrad_loan} onChange={(e) => setFormData({...formData, postgrad_loan: e.target.checked})} disabled={!canEdit} />
              <Label>Postgraduate Loan</Label>
            </div>
          </div>
        </div>
      </div>

      <Separator />

      {/* Right to Work */}
      <div className="space-y-4">
        <h4 className="font-semibold text-slate-700">Right to Work</h4>
        <div className="grid grid-cols-3 gap-4">
          <div>
            <Label>Visa Type</Label>
            <Select value={formData.visa_type} onValueChange={(v) => setFormData({...formData, visa_type: v})} disabled={!canEdit}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="british_citizen">British Citizen</SelectItem>
                <SelectItem value="eu_settled">EU Settled Status</SelectItem>
                <SelectItem value="eu_pre_settled">EU Pre-Settled</SelectItem>
                <SelectItem value="skilled_worker">Skilled Worker Visa</SelectItem>
                <SelectItem value="student">Student Visa</SelectItem>
                <SelectItem value="graduate">Graduate Visa</SelectItem>
                <SelectItem value="family">Family Visa</SelectItem>
              </SelectContent>
            </Select>
          </div>
          {formData.visa_type !== 'british_citizen' && (
            <>
              <div>
                <Label>Visa Expiry</Label>
                <Input type="date" value={formData.visa_expiry} onChange={(e) => setFormData({...formData, visa_expiry: e.target.value})} disabled={!canEdit} />
              </div>
              <div>
                <Label>Share Code</Label>
                <Input value={formData.share_code} onChange={(e) => setFormData({...formData, share_code: e.target.value})} placeholder="XXX-XXX-XXX" disabled={!canEdit} />
              </div>
            </>
          )}
        </div>
      </div>

      <DialogFooter>
        <Button variant="outline" onClick={onClose}>Cancel</Button>
        {canEdit && (
          <Button onClick={handleSubmit} className="bg-[#2a9d8f]">
            Save Employee
          </Button>
        )}
      </DialogFooter>
    </div>
  );
};

export default MoneysoftPayrollPage;
